/**
 * Builds the path autocomplete list for Document Studio.
 *
 * Combines curated suggestions (concrete known documents + collection/subcollection
 * names from references.ts) with two live, debounced Firestore queries: document-id
 * prefix scans (web SDK) for a document slot, and collection-name discovery (backend
 * Admin SDK) for a collection slot — the latter surfaces sub-collections the web SDK
 * cannot enumerate.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { fetchFirestoreCollections } from "../../../../../api/getCall/fetchFirestoreCollections";
import {
  isCollectionPath,
  isDocumentPath,
  suggestDocumentIds,
} from "../utils/documentSource";
import {
  curatedDocumentMatches,
  curatedNextSegments,
  splitEditingPath,
} from "../utils/pathSuggestions";

export interface PathSuggestion {
  /** Full path produced by choosing this suggestion. */
  value: string;
  /** Primary label shown to the user. */
  label: string;
  /** Muted secondary text (the collection or full path). */
  detail: string;
  /** Documents open on select; collections append "/" and keep suggesting. */
  kind: "document" | "collection";
  /** Where the suggestion came from. */
  source: "known" | "live";
}

const DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 100;

export const usePathSuggestions = (value: string): PathSuggestion[] => {
  const [liveIds, setLiveIds] = useState<string[]>([]);
  const [liveCollections, setLiveCollections] = useState<string[]>([]);
  const requestRef = useRef(0);
  const collectionsRequestRef = useRef(0);

  const { base, fragment, index } = useMemo(
    () => splitEditingPath(value),
    [value],
  );

  const isDocumentSlot = index % 2 === 1;
  const canQueryLive = isDocumentSlot && isCollectionPath(base);
  // A collection slot's base must be the root ("") or a document path to list
  // its (sub)collections via the backend Admin SDK.
  const canQueryCollections =
    !isDocumentSlot && (base === "" || isDocumentPath(base));

  useEffect(() => {
    if (!canQueryLive) {
      setLiveIds([]);
      return;
    }
    const request = requestRef.current + 1;
    requestRef.current = request;
    const handle = setTimeout(() => {
      suggestDocumentIds(base, fragment).then((ids) => {
        if (requestRef.current === request) {
          setLiveIds(ids);
        }
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [base, fragment, canQueryLive]);

  useEffect(() => {
    if (!canQueryCollections) {
      setLiveCollections([]);
      return;
    }
    const request = collectionsRequestRef.current + 1;
    collectionsRequestRef.current = request;
    const controller = new AbortController();
    const handle = setTimeout(() => {
      fetchFirestoreCollections(base, controller.signal).then((names) => {
        if (collectionsRequestRef.current === request) {
          setLiveCollections(names);
        }
      });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [base, canQueryCollections]);

  return useMemo<PathSuggestion[]>(() => {
    const prefix = base ? `${base}/` : "";
    const fragmentLower = fragment.toLowerCase();
    const seen = new Set<string>();
    const out: PathSuggestion[] = [];

    const push = (item: PathSuggestion) => {
      if (item.value && !seen.has(item.value)) {
        seen.add(item.value);
        out.push(item);
      }
    };

    for (const id of liveIds) {
      push({
        value: `${prefix}${id}`,
        label: id,
        detail: base,
        kind: "document",
        source: "live",
      });
    }

    for (const name of liveCollections) {
      if (!name.toLowerCase().startsWith(fragmentLower)) {
        continue;
      }
      push({
        value: `${prefix}${name}`,
        label: name,
        detail: `${prefix}${name}/…`,
        kind: "collection",
        source: "live",
      });
    }

    for (const path of curatedDocumentMatches(value)) {
      push({
        value: path,
        label: path.split("/").pop() ?? path,
        detail: path,
        kind: "document",
        source: "known",
      });
    }

    if (!isDocumentSlot) {
      for (const segment of curatedNextSegments(base, fragment, index)) {
        push({
          value: `${prefix}${segment}`,
          label: segment,
          detail: `${prefix}${segment}/…`,
          kind: "collection",
          source: "known",
        });
      }
    }

    return out.slice(0, MAX_SUGGESTIONS);
  }, [value, base, fragment, index, isDocumentSlot, liveIds, liveCollections]);
};
